import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  Chip,
  Slider,
  Stack,
  TextField,
  List,
  ListItem,
  Fade,
  Slide,
  ListItemIcon,
  ListItemText,
  Paper,
  IconButton,
  useMediaQuery
} from '@mui/material';
import { ChevronRight, ArrowUpward, ArrowDownward, DragIndicator } from '@mui/icons-material';
import { TapToRank } from '../ranking/TapToRank';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './styles.scss';

// Types based on backend schema
export interface ExpertiseQuizResult {
  projectTypes: string[];
  projectRankings: { category: string; ranking: number }[];
  experience: string[];
  propertyRankings: { property: string; ranking: number }[];
  otherExperience?: string;
  experienceRankings: { experience: string; ranking: number }[];
  competency: Record<string, string>;
  strengths: { strength: string; ranking: number }[];
}

export interface ExpertiseQuizProps {
  onComplete?: () => void;
  onResultChange?: (result: ExpertiseQuizResult | null) => void;
  onBeginShown?: () => void;
  onQuizStarted?: () => void;
  onStepChange?: (step: number, totalSteps: number) => void;
  initialData?: ExpertiseQuizResult | null;
}

export interface ExpertiseQuizRef {
  nextStep: () => void;
  backStep: () => void;
  reset: () => void;
  getCurrentResult: () => ExpertiseQuizResult | null;
  handleBegin: () => void;
}

// Data from backend enums - matching the order from the provided code
const projectCategories = [
  { value: 'FullHomeDesign', label: 'Full Home Design' },
  { value: 'FamilyRoom', label: 'Living Room' },
  { value: 'LivingRoomDesign', label: 'Living Room Design' },
  { value: 'KitchenDesign', label: 'Kitchen Design' },
  { value: 'DiningRoomDesign', label: 'Dining Room Design' },
  { value: 'EntryDesign', label: 'Entry Design' },
  { value: 'MudroomDesign', label: 'Mudroom Design' },
  { value: 'HomeOffice', label: 'Home Office' },
  { value: 'KidsBedroom', label: 'Kids Bedroom' },
  { value: 'BasementDesign', label: 'Basement Design' },
  { value: 'SpacePlanning', label: 'Space Planning' },
  { value: 'ColorConsulting', label: 'Color Consulting' },
  { value: 'LightingDesign', label: 'Lighting Design' },
  { value: 'FurnitureSelection', label: 'Furniture Selection' },
  { value: 'ArtSelection', label: 'Art Selection' },
  { value: 'AntiqueSelection', label: 'Antique Selection' },
  { value: 'HomeStaging', label: 'Home Staging' },
  { value: 'ConstructionRemodel', label: 'Construction/Remodel' },
  { value: 'EventAndHolidayDecor', label: 'Event/Holiday Décor' },
  { value: 'PrimaryBedroomDesign', label: 'Primary Bedroom Design' },
  { value: 'GuestBedroomDesign', label: 'Guest Bedroom Design' },
  { value: 'PrimaryBathroomDesign', label: 'Primary Bathroom Design' },
  { value: 'OtherBathroomDesign', label: 'Other Bathroom Design' },
  { value: 'OutdoorSpaceDesign', label: 'Outdoor Space Design' },
  { value: 'ProjectManagementDesign', label: 'Project Management + Design' }
];

const experienceTypes = [
  { value: 'PrimaryResidence', label: 'Primary Residence' },
  { value: 'VacationHomeLakeHouse', label: 'Vacation Home - Lake House' },
  { value: 'VacationHomeBeachHouse', label: 'Vacation Home - Beach House' },
  { value: 'VacationHomeLodge', label: 'Vacation Home - Lodge' },
  { value: 'RentalProperty', label: 'Rental Property' },
  { value: 'SmallCommercial', label: 'Small Commercial' },
  { value: 'Other', label: 'Other' }
];

const competencyAreas = [
  { value: 'accessibility', label: 'Accessibility' },
  { value: 'petFriendly', label: 'Pet Friendly' },
  { value: 'smartHome', label: 'Smart Home' },
  { value: 'customStorage', label: 'Custom Storage' },
  { value: 'specificAcoustic', label: 'Specific Acoustic' },
  { value: 'environmentallySustainable', label: 'Environmentally Sustainable' },
  { value: 'uniqueLightingEffects', label: 'Unique Lighting Effects' },
  { value: 'historicalPreservation', label: 'Historical Preservation' },
  { value: 'outdoorLiving', label: 'Outdoor Living' },
  { value: 'specializedVentilation', label: 'Specialized Ventilation' },
  { value: 'fengShui', label: 'Feng Shui' },
  { value: 'artIntegration', label: 'Art Integration' },
  { value: 'wineCellar', label: 'Wine Cellar' },
  { value: 'homeGym', label: 'Home Gym' }
];

const strengthOptions = [
  { value: 'creativity', label: 'Creativity' },
  { value: 'communication', label: 'Communication' },
  { value: 'accessibility', label: 'Accessibility' },
  { value: 'attentionToDetail', label: 'Attention to Detail' },
  { value: 'problemSolving', label: 'Problem Solving' },
  { value: 'timelineManagement', label: 'Timeline Management' },
  { value: 'adaptability', label: 'Adaptability' },
  { value: 'collaboration', label: 'Collaboration' },
  { value: 'projectManagement', label: 'Project Management' },
  { value: 'budgetManagement', label: 'Budget Management' }
];


// Function to get the display label for a project type
const getDisplayLabel = (projectType: string) => {
  const category = projectCategories.find(cat => cat.value === projectType);
  if (category) {
    return category.label;
  }
  // Fallback: add spaces to camelCase or concatenated words
  return projectType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
};

// Function to get the display label for experience types
const getExperienceDisplayLabel = (experienceType: string) => {
  const experience = experienceTypes.find(exp => exp.value === experienceType);
  if (experience) {
    return experience.label;
  }
  // Fallback: add spaces to camelCase or concatenated words
  return experienceType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
};

// Function to get the display label for strength types
const getStrengthsDisplayLabel = (strengthType: string) => {
  const strength = strengthOptions.find(strength => strength.value === strengthType);
  if (strength) {
    return strength.label;
  }
  // Fallback: add spaces to camelCase or concatenated words
  return strengthType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
};

// ProjectRanker component for drag and drop ranking
type ProjectRankerProps = {
  items: string[];
  value: string[];
  onChange: (ordered: string[]) => void;
  getDisplayLabel?: (item: string) => string;
};

const ProjectRanker: React.FC<ProjectRankerProps> = ({ items, value, onChange, getDisplayLabel }) => {
  // Always use items as the source of truth, but preserve order from value if available
  const order = value.length > 0 ? value : items;
  const isSmall = useMediaQuery("(max-width:600px)");


  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length || from === to) return;
    onChange(arrayMove(order, from, to));
  };

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onChange(arrayMove(order, from, to));
  };

  if (isSmall) {
    return (
      <TapToRank 
        items={items} 
        value={value} 
        onChange={onChange}
        getDisplayLabel={getDisplayLabel}
      />
    );
  }

  return (
    <Box maxWidth={720} mx="auto">
      <Typography variant="body2" color="text.secondary" gutterBottom textAlign="center">
        (1 = Highest, {order.length} = Lowest)
      </Typography>

      <Paper variant="outlined" sx={{ p: 0.5, border: "none" }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            <List disablePadding>
              {order.map((label, index) => (
                <SortableRow key={label} id={label} label={getDisplayLabel ? getDisplayLabel(label) : label} rank={index + 1} />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      </Paper>
    </Box>
  );
};

function SortableRow({ id, label, rank }: { id: string; label: string; rank: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <ListItem
      ref={setNodeRef}
      disableGutters
      {...attributes}
      {...listeners}
      sx={{
        px: 1.5,
        py: 1,
        "& + li": { mt: 0.5 },
        opacity: isDragging ? 0.6 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        bgcolor: "background.paper",
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        touchAction: "none",
        border: "none",
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, color: "text.disabled" }}>
        <Typography variant="body2" fontWeight="bold" sx={{ 
          color: "text.primary", 
          width: 24, 
          height: 24, 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          fontSize: "0.75rem",
          mr: 1
        }}>
          {rank}
        </Typography>
      </ListItemIcon>

      <ListItemText primary={getDisplayLabel(label)} />

      <ListItemIcon sx={{ minWidth: 28, color: "text.disabled", justifyContent: "flex-end" }}>
        <DragIndicator fontSize="small" sx={{ width: 16, height: 16 }} />
      </ListItemIcon>
    </ListItem>
  );
}

export const ExpertiseQuiz = forwardRef<ExpertiseQuizRef, ExpertiseQuizProps>(({ 
  onComplete, 
  onResultChange, 
  onBeginShown, 
  onQuizStarted, 
  onStepChange,
  initialData
}, ref) => {
  const [showBegin, setShowBegin] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 7; // Project selection, project ranking, property selection, property ranking, competencies (all at once), strengths selection, strengths ranking

  // Step data
  const [selectedProjectTypes, setSelectedProjectTypes] = useState<string[]>([]);
  const selectedProjectTypesRef = useRef<string[]>([]);
  const [projectRankings, setProjectRankings] = useState<string[]>([]);
  const projectRankingsRef = useRef<string[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<string[]>([]);
  const selectedExperienceRef = useRef<string[]>([]);
  const [propertyRankings, setPropertyRankings] = useState<string[]>([]);
  const propertyRankingsRef = useRef<string[]>([]);
  const [otherExperience, setOtherExperience] = useState<string>('');
  const otherExperienceRef = useRef<string>('');
  const [competency, setCompetency] = useState<Record<string, string>>({});
  const competencyRef = useRef<Record<string, string>>({});
  const [selectedStrengths, setSelectedStrengths] = useState<string[]>([]);
  const selectedStrengthsRef = useRef<string[]>([]);
  const [strengthsRankings, setStrengthsRankings] = useState<string[]>([]);
  const strengthsRankingsRef = useRef<string[]>([]);
  const hasInteractedRef = useRef<boolean>(false);
  const hasPrefilledRef = useRef<boolean>(false);
  const lastInitialDataRef = useRef<any>(null);

  const [isCompleted, setIsCompleted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [animationDirection, setAnimationDirection] = useState<'in' | 'out'>('in');

  // Notify parent about step changes (only when user navigates, not during render)
  const notifyStepChange = useCallback(() => {
    if (!showBegin && onStepChange) {
      onStepChange(currentStep, totalSteps);
    }
  }, [currentStep, showBegin, onStepChange, totalSteps]);

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      // Notify parent after state update
      setTimeout(() => notifyStepChange(), 0);
    }
  }, [currentStep, totalSteps, notifyStepChange]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      // Notify parent after state update
      setTimeout(() => notifyStepChange(), 0);
    }
  }, [currentStep, notifyStepChange]);

  // Create stable callback functions
  const handleNextCallback = useCallback(() => {
    if (!showBegin && currentStep <= totalSteps) {
      handleNext();
    }
  }, [showBegin, currentStep, totalSteps, handleNext]);

  const handleBackCallback = useCallback(() => {
    if (!showBegin && currentStep > 1) {
      handleBack();
    }
  }, [showBegin, currentStep, handleBack]);

  const handleBegin = useCallback(() => {
    setShowBegin(false);
    if (onQuizStarted) onQuizStarted();
  }, [onQuizStarted]);


  const handleReset = () => {
    setSelectedProjectTypes([]);
    setProjectRankings([]);
    setSelectedExperience([]);
    setPropertyRankings([]);
    setOtherExperience('');
    setCompetency({});
    setSelectedStrengths([]);
    setStrengthsRankings([]);
    setShowBegin(true);
    setCurrentStep(1);
    hasInteractedRef.current = false;
    hasPrefilledRef.current = false;
    if (onResultChange) onResultChange(null);
  };

  // Get current result using refs to ensure we always have the latest data
  const getCurrentResult = useCallback((): ExpertiseQuizResult | null => {
    if (showBegin) return null;
    
    return {
      projectTypes: selectedProjectTypesRef.current,
      projectRankings: projectRankingsRef.current.map((type, index) => ({
        category: type,
        ranking: index + 1
      })),
      experience: selectedExperienceRef.current,
      propertyRankings: propertyRankingsRef.current.map((property, index) => ({
        property,
        ranking: index + 1
      })),
      otherExperience: otherExperienceRef.current || undefined,
      experienceRankings: selectedExperienceRef.current.map((exp, index) => ({
        experience: exp,
        ranking: index + 1
      })),
      competency: competencyRef.current,
      strengths: strengthsRankingsRef.current.map((strength, index) => ({
        strength,
        ranking: index + 1
      }))
    };
  }, [showBegin]);

  // Expose functions to parent component via ref
  useImperativeHandle(ref, () => ({
    nextStep: handleNextCallback,
    backStep: handleBackCallback,
    reset: handleReset,
    getCurrentResult,
    handleBegin
  }), [handleNextCallback, handleBackCallback, getCurrentResult, handleBegin]);

  // Check completion status
  // Only mark as completed when user actually reaches the final step AND has interacted
  // Don't auto-complete when preloading data - user must manually navigate through all steps
  useEffect(() => {
    // Only consider completed if we're on the final step AND the user has made selections
    // This prevents auto-advance when preloading data
    // IMPORTANT: Don't auto-complete - only complete when user explicitly clicks Continue
    const hasSelections = selectedProjectTypes.length > 0 && 
                          selectedExperience.length > 0 && 
                          Object.keys(competency).length > 0 &&
                          selectedStrengths.length > 0;
    const completed = !showBegin && currentStep >= totalSteps && hasSelections && hasInteractedRef.current;
    if (completed !== isCompleted) {
      setIsCompleted(completed);
      // Don't auto-trigger onComplete - let the parent's Continue button handle it
    }
  }, [currentStep, showBegin, isCompleted, totalSteps, selectedProjectTypes, selectedExperience, competency, selectedStrengths]);

  // Keep refs in sync with state
  useEffect(() => {
    selectedProjectTypesRef.current = selectedProjectTypes;
  }, [selectedProjectTypes]);
  useEffect(() => {
    projectRankingsRef.current = projectRankings;
  }, [projectRankings]);
  useEffect(() => {
    selectedExperienceRef.current = selectedExperience;
  }, [selectedExperience]);
  useEffect(() => {
    propertyRankingsRef.current = propertyRankings;
  }, [propertyRankings]);
  useEffect(() => {
    otherExperienceRef.current = otherExperience;
  }, [otherExperience]);
  useEffect(() => {
    competencyRef.current = competency;
  }, [competency]);
  useEffect(() => {
    selectedStrengthsRef.current = selectedStrengths;
  }, [selectedStrengths]);
  useEffect(() => {
    strengthsRankingsRef.current = strengthsRankings;
  }, [strengthsRankings]);

  // Update result when data changes (only when user makes selections, not during render)
  const notifyResultChange = useCallback(() => {
    if (!showBegin && onResultChange) {
      const result: ExpertiseQuizResult = {
        projectTypes: selectedProjectTypesRef.current, // Use ref to get latest value
        projectRankings: projectRankingsRef.current.map((type, index) => ({
          category: type,
          ranking: index + 1
        })),
        experience: selectedExperienceRef.current, // Use ref to get latest value
        propertyRankings: propertyRankingsRef.current.map((property, index) => ({
          property,
          ranking: index + 1
        })),
        otherExperience: otherExperienceRef.current || undefined, // Use ref to get latest value
        experienceRankings: selectedExperienceRef.current.map((exp, index) => ({
          experience: exp,
          ranking: index + 1
        })),
        competency: competencyRef.current, // Use ref to get latest value
        strengths: strengthsRankingsRef.current.map((strength, index) => ({
          strength,
          ranking: index + 1
        }))
      };
      onResultChange(result);
    }
  }, [showBegin, onResultChange]); // Removed all state dependencies since we're using refs

  // Watch for any state changes (including clearing selections) and notify parent
  useEffect(() => {
    if (!showBegin) {
      setTimeout(() => notifyResultChange(), 0);
    }
  }, [
    showBegin,
    selectedProjectTypes,
    projectRankings,
    selectedExperience,
    propertyRankings,
    otherExperience,
    competency,
    selectedStrengths,
    strengthsRankings,
    notifyResultChange
  ]);

  // Prefill quiz when initialData is provided and quiz hasn't been interacted with yet
  // NOTE: This must come AFTER notifyResultChange is defined
  // IMPORTANT: Only prefill when initialData actually changes, not when state changes
  useEffect(() => {
    // Check if initialData has actually changed
    const initialDataChanged = initialData !== lastInitialDataRef.current;
    

    if (initialData && !hasInteractedRef.current && initialDataChanged) {
      // Check if we need to prefill (only if initialData has changed and we haven't prefilled yet)
      const needsPrefill = !hasPrefilledRef.current;

      if (needsPrefill) {
        
        // Prefill all the quiz data from initialData
        // IMPORTANT: Update refs immediately along with state to avoid race conditions
        if (initialData.projectTypes) {
          const projectTypes = initialData.projectTypes;
          setSelectedProjectTypes(projectTypes);
          selectedProjectTypesRef.current = projectTypes;
          if (Array.isArray(initialData.projectRankings)) {
            const rankings = initialData.projectRankings.map((r: any) => r.category);
            setProjectRankings(rankings);
            projectRankingsRef.current = rankings;
          }
        }
        if (initialData.experience) {
          const experience = initialData.experience;
          setSelectedExperience(experience);
          selectedExperienceRef.current = experience;
          if (Array.isArray(initialData.experienceRankings)) {
            const rankings = initialData.experienceRankings.map((r: any) => r.experience);
            setPropertyRankings(rankings);
            propertyRankingsRef.current = rankings;
          }
        }
        if (initialData.otherExperience) {
          setOtherExperience(initialData.otherExperience);
          otherExperienceRef.current = initialData.otherExperience;
        }
        if (initialData.competency && Object.keys(initialData.competency).length > 0) {
          setCompetency(initialData.competency);
          competencyRef.current = initialData.competency;
        } else {
        }
        if (Array.isArray(initialData.strengths) && initialData.strengths.length > 0) {
          const strengthValues = initialData.strengths.map((s: any) => s?.strength || s);
          setSelectedStrengths(strengthValues);
          selectedStrengthsRef.current = strengthValues;
          // Set rankings from the saved order (preserve the original ranking order)
          // The strengths array from initialData is already in ranked order
          setStrengthsRankings(strengthValues);
          strengthsRankingsRef.current = strengthValues;
        } else {
        }
        hasPrefilledRef.current = true;
        lastInitialDataRef.current = initialData;
        // Notify parent of the preloaded result
        // Now that refs are synced, this will have the correct data
        setTimeout(() => notifyResultChange(), 0);
      } else {
      }
    } else {
      if (!initialDataChanged) {
      } else {
      }
    }
  }, [initialData, notifyResultChange]);

  // Initialize project rankings when project types are selected
  useEffect(() => {
    if (selectedProjectTypes.length > 0) {
      // Always update rankings when project types change, but preserve existing order if possible
      if (projectRankings.length === 0) {
        setProjectRankings(selectedProjectTypes);
      } else {
        // Update rankings to include any new selections while preserving existing order
        const newRankings = [...projectRankings];
        selectedProjectTypes.forEach(type => {
          if (!newRankings.includes(type)) {
            newRankings.push(type);
          }
        });
        // Remove any types that are no longer selected
        const filteredRankings = newRankings.filter(type => selectedProjectTypes.includes(type));
        setProjectRankings(filteredRankings);
      }
    } else if (projectRankings.length > 0) {
      // Clear rankings when all selections are removed
      setProjectRankings([]);
    }
  }, [selectedProjectTypes, projectRankings]);

  // Initialize property rankings when experience types are selected
  useEffect(() => {
    if (selectedExperience.length > 0) {
      // Always update rankings when experience types change, but preserve existing order if possible
      if (propertyRankings.length === 0) {
        setPropertyRankings(selectedExperience);
      } else {
        // Update rankings to include any new selections while preserving existing order
        const newRankings = [...propertyRankings];
        selectedExperience.forEach(exp => {
          if (!newRankings.includes(exp)) {
            newRankings.push(exp);
          }
        });
        // Remove any types that are no longer selected
        const filteredRankings = newRankings.filter(exp => selectedExperience.includes(exp));
        setPropertyRankings(filteredRankings);
      }
    } else if (propertyRankings.length > 0) {
      // Clear rankings when all selections are removed
      setPropertyRankings([]);
    }
  }, [selectedExperience, propertyRankings]);

  // Initialize strengths rankings when strengths are selected
  // IMPORTANT: Only initialize rankings if we haven't prefilled yet, or if user is interacting
  // This prevents overwriting preloaded rankings when the component first loads
  useEffect(() => {
    if (selectedStrengths.length > 0 && (hasInteractedRef.current || !hasPrefilledRef.current)) {
      // Always update rankings when strengths change, but preserve existing order if possible
      if (strengthsRankings.length === 0) {
        setStrengthsRankings(selectedStrengths);
      } else {
        // Update rankings to include any new selections while preserving existing order
        const newRankings = [...strengthsRankings];
        selectedStrengths.forEach(strength => {
          if (!newRankings.includes(strength)) {
            newRankings.push(strength);
          }
        });
        // Remove any strengths that are no longer selected
        const filteredRankings = newRankings.filter(strength => selectedStrengths.includes(strength));
        setStrengthsRankings(filteredRankings);
      }
    } else if (selectedStrengths.length === 0 && strengthsRankings.length > 0) {
      // Clear rankings when all selections are removed
      setStrengthsRankings([]);
    }
  }, [selectedStrengths, strengthsRankings]);

  const toggleProjectType = (type: string) => {
    hasInteractedRef.current = true;
    setSelectedProjectTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
    // The useEffect watching selectedProjectTypes will notify parent when state updates
  };

  const toggleExperience = (exp: string) => {
    hasInteractedRef.current = true;
    setSelectedExperience(prev => 
      prev.includes(exp) 
        ? prev.filter(e => e !== exp)
        : [...prev, exp]
    );
    // The useEffect watching selectedExperience will notify parent when state updates
  };

  const toggleStrength = (strength: string) => {
    hasInteractedRef.current = true;
    setSelectedStrengths(prev => 
      prev.includes(strength) 
        ? prev.filter(s => s !== strength)
        : [...prev, strength]
    );
    // The useEffect watching selectedStrengths will notify parent when state updates
  };

  const setCompetencyLevel = (area: string, level: string) => {
    hasInteractedRef.current = true;
    setCompetency(prev => {
      const newCompetency = {
        ...prev,
        [area]: level
      };
      return newCompetency;
    });
    // The useEffect watching competency will notify parent when state updates
  };

  // Notify parent when begin screen is shown
  useEffect(() => {
    if (showBegin && onBeginShown) {
      onBeginShown();
    }
  }, [showBegin, onBeginShown]);

  // Notify parent about step changes when component loads or step changes
  useEffect(() => {
    if (!showBegin && onStepChange) {
      onStepChange(currentStep, totalSteps);
    }
  }, [currentStep, showBegin, onStepChange, totalSteps]);

  // Show Begin screen
  if (showBegin) {
    
    return (
      <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
        <Box textAlign="center" pt={0} pb={6}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
            Expertise Assessment
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Help us understand your design expertise and specializations.<br/> This information will help match you with the right clients and projects.
          </Typography>
          <Button 
            variant="outlined" 
            size="large" 
            color="secondary"
            endIcon={<ChevronRight sx={{ width: 16, height: 16 }} />}
            onClick={handleBegin}
          >
            Begin
          </Button>
        </Box>
      </Container>
    );
  }

  const renderStep1 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          What types of projects are you interested in?
        </Typography>
        <Typography variant="body1" color="text.secondary">
          (Select All That Apply)
        </Typography>
      </Box>

      <Box maxWidth="800px" mx="auto">
        <Box 
          className="tag-grid" 
          display="flex" 
          flexWrap="wrap" 
          gap={1} 
          justifyContent="start"
        >
          {projectCategories.map((category) => (
            <Chip
              key={category.value}
              className="tag-chip"
              label={category.label}
              onClick={() => toggleProjectType(category.value)}
              variant={selectedProjectTypes.includes(category.value) ? 'filled' : 'outlined'}
              color='secondary'
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                py: 0.25,
                px: 0.5,
                height: 'auto',
                minWidth: 'auto',
                width: 'fit-content',
                maxWidth: 'none',
                borderRadius: 50,
                borderWidth: 1,
                fontWeight: 500,
                '& .MuiChip-label': {
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                  textOverflow: 'unset',
                  maxWidth: 'none',
                  width: 'auto',
                  padding: '4px 4px'
                },
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white',
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            />
          ))}
        </Box>
      </Box>

    </Container>
  );

  const renderStep2 = () => (
    <Container maxWidth="lg" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Great! Now, drag and drop your selections to rank them.
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Great! Now, tap items to rank them in order.
          </Box>
        </Typography>
      </Box>

      <ProjectRanker 
        items={selectedProjectTypes}
        value={projectRankings}
        onChange={(newRankings) => {
          hasInteractedRef.current = true; // Mark as interacted when user changes rankings
          setProjectRankings(newRankings);
          // The useEffect watching projectRankings will notify parent when state updates
        }}
      />

    </Container>
  );

  const renderStep3 = () => (
    <Container maxWidth="lg" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          What types of properties have you worked on?
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select all that apply
        </Typography>
      </Box>

      <Box maxWidth="800px" mx="auto">
        <Box className="tag-grid" display="flex" flexWrap="wrap" gap={1} justifyContent="start">
          {experienceTypes.map((exp) => (
            <Chip
              key={exp.value}
              className="tag-chip"
              label={exp.label}
              onClick={() => toggleExperience(exp.value)}
              variant={selectedExperience.includes(exp.value) ? 'filled' : 'outlined'}
              color='secondary'
              sx={{
                fontSize: '0.9rem',
                py: 0.5, // 8px
                px: 0.5, // 8px
                height: 'auto',
                minWidth: 'auto',
                width: 'fit-content',
                borderRadius: 50,
                borderWidth: 2,
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white',
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            />
          ))}
        </Box>
      </Box>

      {selectedExperience.includes('Other') && (
        <Box mt={4}>
          <TextField
            fullWidth
            label="Please specify other experience"
            value={otherExperience}
            onChange={(e) => {
              setOtherExperience(e.target.value);
              // The useEffect watching otherExperience will notify parent when state updates
            }}
            multiline
            rows={3}
          />
        </Box>
      )}
    </Container>
  );


  const renderStep4 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Great! Now, drag and drop your selections to rank them.
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Great! Now, tap items to rank them in order.
          </Box>
        </Typography>
      </Box>

      <ProjectRanker 
        items={selectedExperience}
        value={propertyRankings}
        onChange={(newRankings) => {
          hasInteractedRef.current = true; // Mark as interacted when user changes rankings
          setPropertyRankings(newRankings);
          // The useEffect watching propertyRankings will notify parent when state updates
        }}
        getDisplayLabel={getExperienceDisplayLabel}
      />
    </Container>
  );

  // Render all competencies at once
  const renderCompetencies = () => {
    
    return (
      <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
            Rate your design competency in the following specialty areas.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            (Select Competency Level for Each)
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {competencyAreas.map((area, index) => {
            const currentValue = competency[area.value];
            return (
          <Box 
            key={area.value} 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              minHeight: { xs: 'auto', sm: 44 },
              mb: 1.5, // 12px gap
              py: 1
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                flexGrow: 1,
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: 500,
                mb: { xs: 1, sm: 0 }
              }}
            >
              {index + 1}. {area.label}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1.5, 
              ml: { xs: 0, sm: 2 },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant={competency[area.value] === 'None' ? 'contained' : 'outlined'}
                onClick={() => {
                  setCompetency(prev => {
                    const newState = { ...prev, [area.value]: 'None' };
                    return newState;
                  });
                  // The useEffect watching competency will notify parent when state updates
                }}
                sx={{
                  height: 44,
                  minWidth: { xs: 'auto', sm: 80 },
                  flex: { xs: 1, sm: 'none' },
                  fontSize: { xs: '14px', sm: '16px' },
                  backgroundColor: competency[area.value] === 'None' ? '#D22268' : 'transparent',
                  color: competency[area.value] === 'None' ? 'white' : '#D22268',
                  borderColor: '#D22268',
                  '&:hover': {
                    backgroundColor: competency[area.value] === 'None' ? '#B01E5A' : '#D2226810',
                    borderColor: '#D22268',
                  }
                }}
              >
                None
              </Button>
              <Button
                variant={competency[area.value] === 'Basic' ? 'contained' : 'outlined'}
                onClick={() => {
                  setCompetency(prev => {
                    const newState = { ...prev, [area.value]: 'Basic' };
                    return newState;
                  });
                  // The useEffect watching competency will notify parent when state updates
                }}
                sx={{
                  height: 44,
                  minWidth: { xs: 'auto', sm: 80 },
                  flex: { xs: 1, sm: 'none' },
                  fontSize: { xs: '14px', sm: '16px' },
                  backgroundColor: competency[area.value] === 'Basic' ? '#D22268' : 'transparent',
                  color: competency[area.value] === 'Basic' ? 'white' : '#D22268',
                  borderColor: '#D22268',
                  '&:hover': {
                    backgroundColor: competency[area.value] === 'Basic' ? '#B01E5A' : '#D2226810',
                    borderColor: '#D22268',
                  }
                }}
              >
                Basic
              </Button>
              <Button
                variant={competency[area.value] === 'Pro' ? 'contained' : 'outlined'}
                onClick={() => {
                  setCompetency(prev => {
                    const newState = { ...prev, [area.value]: 'Pro' };
                    return newState;
                  });
                  // The useEffect watching competency will notify parent when state updates
                }}
                sx={{
                  height: 44,
                  minWidth: { xs: 'auto', sm: 80 },
                  flex: { xs: 1, sm: 'none' },
                  fontSize: { xs: '14px', sm: '16px' },
                  backgroundColor: competency[area.value] === 'Pro' ? '#D22268' : 'transparent',
                  color: competency[area.value] === 'Pro' ? 'white' : '#D22268',
                  borderColor: '#D22268',
                  '&:hover': {
                    backgroundColor: competency[area.value] === 'Pro' ? '#B01E5A' : '#D2226810',
                    borderColor: '#D22268',
                  }
                }}
              >
                Pro
              </Button>
            </Box>
          </Box>
            );
          })}
        </Box>
      </Container>
    );
  };

  const renderStep5 = () => {
    
    return (
    <Container maxWidth="lg" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          What are your top strengths?
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select up to 5 strengths
        </Typography>
      </Box>

      <Box maxWidth="800px" mx="auto">
        <Box className="tag-grid" display="flex" flexWrap="wrap" gap={1} justifyContent="start">
          {strengthOptions.map((strength) => {
            const isSelected = selectedStrengths.includes(strength.value);
            return (
              <Chip
                key={strength.value}
                className="tag-chip"
                label={strength.label}
                onClick={() => toggleStrength(strength.value)}
                variant={isSelected ? 'filled' : 'outlined'}
                color='secondary'
                disabled={selectedStrengths.length >= 5 && !isSelected}
                sx={{
                  fontSize: '0.9rem',
                  py: 0.5, // 8px
                  px: 0.5, // 8px
                  height: 'auto',
                  minWidth: 'auto',
                  width: 'fit-content',
                  borderRadius: 50,
                  borderWidth: 2,
                  opacity: selectedStrengths.length >= 5 && !isSelected ? 0.6 : 1,
                  '&:hover': {
                    backgroundColor: 'secondary.main',
                    color: 'white',
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>

      {selectedStrengths.length > 0 && (
        <Box mt={4}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Selected: {selectedStrengths.length}/5
          </Typography>
        </Box>
      )}
    </Container>
    );
  };


  const renderStep6 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Great! Now, drag and drop your selections to rank them.
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Great! Now, tap items to rank them in order.
          </Box>
        </Typography>
      </Box>

      <ProjectRanker 
        items={selectedStrengths}
        value={strengthsRankings}
        onChange={(newRankings) => {
          hasInteractedRef.current = true; // Mark as interacted when user changes rankings
          setStrengthsRankings(newRankings);
          // The useEffect watching strengthsRankings will notify parent when state updates
        }}
        getDisplayLabel={getStrengthsDisplayLabel}
      />
    </Container>
  );

  return (
    <Box sx={{ pb: 10 }}>
      <Box pt={0} pb={4}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderCompetencies()}
        {currentStep === 6 && renderStep5()}
        {currentStep === 7 && renderStep6()}
      </Box>
    </Box>
  );
});

ExpertiseQuiz.displayName = 'ExpertiseQuiz';

export { ProjectRanker };
export default ExpertiseQuiz;
